/**
 * Plain-`number` formatting helpers for the shared Charts components.
 *
 * These intentionally operate on `number` (not `BigDecimal`) because
 * Recharts hands tick / tooltip callbacks raw numeric values, and the
 * chart axes/tooltips are presentational. They mirror the conventions
 * the YaaS Client Console reporting charts established so the extracted
 * components render identically.
 *
 * For token-amount business logic elsewhere, use the BigDecimal-based
 * helpers in `@solomon-apps/shared/src/utils/format` — these are for
 * chart axes/tooltips only.
 */

type DateInput = number | string | Date;

/** Month + day. e.g. `Jan 15`. */
export function formatShortDate(value: DateInput): string {
    return new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
    });
}

/** Month + day + year. e.g. `Jan 15, 2026`. */
export function formatLongDate(value: DateInput): string {
    return new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
}

/** Compact axis or headline number. e.g. `1.2M`. */
export function formatCompactNumber(value: number): string {
    return new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1
    }).format(value);
}

/** Full-precision chart tooltip amount with its token symbol. */
export function formatChartTokenAmount(value: number, unit: string): string {
    return `${value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })} ${unit}`;
}

/** Compact chart headline amount with its token symbol. */
export function formatCompactTokenAmount(value: number, unit: string): string {
    return `${formatCompactNumber(value)} ${unit}`;
}

export function formatUsd(value: number, options?: { fractionDigits?: 0 | 2 }): string {
    const fractionDigits = options?.fractionDigits ?? 2;
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
    }).format(value);
}

export function formatPct(value: number, fractionDigits = 2): string {
    return `${value.toFixed(fractionDigits)}%`;
}
